import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, FormControlLabel, Switch, Divider, Alert,
  IconButton, InputAdornment, CircularProgress, LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { createPartnerUser } from '../../services/partnerUserService';
import {
  getPartnerConfig,
  getPartnerCreateLabel,
  getPartnerAssignedHub,
  isPartnerEntityType,
  SERVICE_HUB_OPTIONS,
} from '../../constants/partnerServices';
import { getUserTypeFormConfig } from '../../constants/userManagement';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import { composeE164 } from '../../utils/phoneDialCode';
import {
  validatePartnerContactForm,
  partnerValidationSummary,
  clearPartnerFieldErrors,
} from '../../utils/partnerFormValidation';
import { PERSON_NAME_MIN, PERSON_NAME_MAX } from '../../utils/personFieldValidation';

interface ZoneOption { _id: string; name: string; code?: string }

interface PartnerUserCreateDrawerProps {
  open: boolean;
  onClose: () => void;
  entityType: string;
  zones: ZoneOption[];
}

function mapCreateError(error: unknown): { message: string; fieldErrors: Record<string, string> } {
  const resp = (error as { response?: { data?: { message?: string; errors?: Record<string, string> } } })?.response?.data;
  const message = resp?.message || 'Failed to create partner';
  const fieldErrors: Record<string, string> = { ...(resp?.errors || {}) };
  const lower = message.toLowerCase();
  if (!fieldErrors.email && lower.includes('email') && lower.includes('exists')) {
    fieldErrors.email = 'An account with this email already exists';
  }
  if (!fieldErrors.phone && lower.includes('phone') && lower.includes('exists')) {
    fieldErrors.phone = 'An account with this phone number already exists';
  }
  if (!fieldErrors.email && !fieldErrors.phone && !fieldErrors.password && lower.includes('already exists')) {
    fieldErrors.contact = message;
  }
  if (!fieldErrors.password && lower.includes('password')) {
    if (lower.includes('required')) fieldErrors.password = 'Password is required';
    else if (lower.includes('6')) fieldErrors.password = 'Password must be at least 6 characters';
  }
  return { message, fieldErrors };
}

export default function PartnerUserCreateDrawer({ open, onClose, entityType, zones }: PartnerUserCreateDrawerProps) {
  const queryClient = useQueryClient();
  const config = getPartnerConfig(entityType);
  const partnerLabel = getPartnerCreateLabel(entityType);
  const requiresZone = isPartnerEntityType(entityType)
    || getUserTypeFormConfig(config?.userType || entityType).requiresZone;
  const assignedServiceHub = getPartnerAssignedHub(entityType);
  const assignedHubLabel = SERVICE_HUB_OPTIONS.find((h) => h.value === assignedServiceHub)?.label || assignedServiceHub;
  const [form, setForm] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    phoneCountryCode: string;
    password: string;
    zoneId: string;
    autoVerify: boolean;
    sendWelcomeEmail: boolean;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountryCode: '',
    password: '',
    zoneId: '',
    autoVerify: false,
    sendWelcomeEmail: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const submitLockRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);

  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      const first = formRef.current?.querySelector<HTMLElement>(
        '.Mui-error input, .Mui-error textarea, .Mui-error [role="combobox"]',
      );
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first?.focus({ preventScroll: true });
    });
  };

  const patchErrors = (keys: string[]) => {
    setErrors((prev) => clearPartnerFieldErrors(prev, keys));
  };

  const resetForm = () => {
    submitLockRef.current = false;
    setErrors({});
    setSuccessMessage('');
    setConfirmPassword('');
    setShowPassword(false);
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      phoneCountryCode: '',
      password: '',
      zoneId: '',
      autoVerify: false,
      sendWelcomeEmail: true,
    });
  };

  useEffect(() => {
    if (open) resetForm();
  }, [open, entityType]);

  const mutation = useMutation({
    mutationFn: () => {
      const phoneE164 = form.phone.trim()
        ? composeE164(form.phoneCountryCode, form.phone)
        : undefined;
      return createPartnerUser({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: phoneE164,
        phoneCountryCode: form.phoneCountryCode || undefined,
        entityType,
        zoneId: form.zoneId,
        serviceHub: assignedServiceHub,
        password: form.password.trim() || undefined,
        autoVerify: form.autoVerify,
        sendWelcomeEmail: form.sendWelcomeEmail,
        metadata: {
          phoneCountryCode: form.phoneCountryCode || undefined,
          sendWelcomeEmail: form.sendWelcomeEmail,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      const welcomeNote = form.sendWelcomeEmail && form.email.trim()
        ? ` A welcome message was sent to ${form.email.trim()}.`
        : form.sendWelcomeEmail && form.phone.trim()
          ? ' A welcome SMS was sent to the registered phone number.'
          : '';
      setSuccessMessage(`Partner created successfully.${welcomeNote}`);
      setTimeout(() => {
        resetForm();
        mutation.reset();
        onClose();
      }, 1800);
    },
    onError: (error) => {
      const { fieldErrors, message } = mapCreateError(error);
      setErrors((prev) => ({ ...prev, ...fieldErrors }));
      if (!Object.keys(fieldErrors).length && message) {
        setErrors((prev) => ({ ...prev, contact: message }));
      }
      scrollToFirstError();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    resetForm();
    mutation.reset();
    onClose();
  };

  const handleSubmit = () => {
    if (submitLockRef.current || mutation.isPending || successMessage) return;
    if (!validate()) return;
    submitLockRef.current = true;
    mutation.mutate(undefined, {
      onSettled: () => {
        submitLockRef.current = false;
      },
    });
  };

  const validate = () => {
    const next = validatePartnerContactForm(
      {
        ...form,
        confirmPassword,
      },
      {
        requiresZone,
        zones,
        requirePassword: true,
      },
    );
    setErrors(next);
    if (Object.keys(next).length) scrollToFirstError();
    return Object.keys(next).length === 0;
  };

  const validationLines = partnerValidationSummary(errors);

  const nameHelper = `Letters only · ${PERSON_NAME_MIN}–${PERSON_NAME_MAX} characters`;
  const formDisabled = mutation.isPending || !!successMessage;

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        if (mutation.isPending) return;
        handleClose();
      }}
      ModalProps={{ disableRestoreFocus: true }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Add {partnerLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Admin account form — not the mobile registration flow. Creates a {partnerLabel.toLowerCase()} assigned to the {assignedHubLabel} hub.
        </Typography>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, fontWeight: 700 }}>{successMessage}</Alert>
        )}
        {validationLines.length > 0 && !successMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Please fix the following ({validationLines.length}):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {validationLines.map((line) => (
                <Typography key={line} component="li" variant="body2">{line}</Typography>
              ))}
            </Box>
          </Alert>
        )}
        {mutation.isError && !successMessage && !validationLines.length && (
          <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>
            {mapCreateError(mutation.error).message}
          </Alert>
        )}
        <Stack
          component="form"
          ref={formRef}
          spacing={2}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <PersonNameField
            label="First Name"
            required
            fullWidth
            size="small"
            disabled={formDisabled}
            value={form.firstName}
            error={!!errors.firstName}
            helperText={errors.firstName || nameHelper}
            inputProps={{ maxLength: PERSON_NAME_MAX }}
            onChange={(e) => {
              patchErrors(['firstName']);
              setForm({ ...form, firstName: e.target.value });
            }}
          />
          <PersonNameField
            label="Last Name"
            fullWidth
            size="small"
            disabled={formDisabled}
            value={form.lastName}
            error={!!errors.lastName}
            helperText={errors.lastName || nameHelper}
            inputProps={{ maxLength: PERSON_NAME_MAX }}
            onChange={(e) => {
              patchErrors(['lastName']);
              setForm({ ...form, lastName: e.target.value });
            }}
          />
          <TextField
            label="Email"
            type="email"
            required={!form.phone.trim()}
            fullWidth
            size="small"
            disabled={formDisabled}
            value={form.email}
            error={!!errors.email || !!errors.contact}
            helperText={
              errors.email
              || errors.contact
              || (form.sendWelcomeEmail ? 'Validated on save · welcome email sent here when configured' : 'Validated on save')
            }
            autoComplete="off"
            onChange={(e) => {
              patchErrors(['email', 'contact']);
              setForm({ ...form, email: e.target.value });
            }}
          />
          <PhoneWithDialCodeField
            label="Phone"
            required={!form.email.trim()}
            phone={form.phone}
            countryCode={form.phoneCountryCode}
            preferredCountry="IN"
            error={!!errors.phone || !!errors.contact}
            helperText={errors.phone || errors.contact}
            disabled={formDisabled}
            onChange={({ phone, countryCode }) => {
              patchErrors(['phone', 'contact']);
              setForm({
                ...form,
                phone,
                phoneCountryCode: countryCode,
              });
            }}
          />
          <TextField
            label="Password"
            required
            type={showPassword ? 'text' : 'password'}
            fullWidth
            size="small"
            disabled={formDisabled}
            value={form.password}
            error={!!errors.password}
            helperText={errors.password || 'Required · min 6 characters'}
            autoComplete="new-password"
            inputProps={{
              autoComplete: 'new-password',
              readOnly: true,
              onFocus: (e) => { e.target.readOnly = false; },
            }}
            onChange={(e) => {
              patchErrors(['password', 'confirmPassword']);
              setForm({ ...form, password: e.target.value });
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    disabled={formDisabled}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm Password"
            required
            type={showPassword ? 'text' : 'password'}
            fullWidth
            size="small"
            disabled={formDisabled}
            value={confirmPassword}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword || 'Re-enter the password to confirm'}
            autoComplete="new-password"
            inputProps={{
              autoComplete: 'new-password',
              readOnly: true,
              onFocus: (e) => { e.target.readOnly = false; },
            }}
            onChange={(e) => {
              patchErrors(['confirmPassword']);
              setConfirmPassword(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    disabled={formDisabled}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Zone"
            required={requiresZone}
            fullWidth
            size="small"
            disabled={formDisabled}
            value={form.zoneId}
            error={!!errors.zoneId}
            helperText={
              errors.zoneId
              || (requiresZone
                ? (zones.length ? 'Required — assign the partner to a service zone' : 'No zones available — create a zone first')
                : '')
            }
            onChange={(e) => {
              patchErrors(['zoneId']);
              setForm({ ...form, zoneId: e.target.value });
            }}
          >
            <MenuItem value="" disabled={requiresZone}>
              {requiresZone ? 'Select a zone' : 'No zone'}
            </MenuItem>
            {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={form.autoVerify} disabled={formDisabled} onChange={(e) => setForm({ ...form, autoVerify: e.target.checked })} />}
            label="Auto-verify on create (skip document approval)"
          />
          <FormControlLabel
            control={<Switch checked={form.sendWelcomeEmail} disabled={formDisabled} onChange={(e) => setForm({ ...form, sendWelcomeEmail: e.target.checked })} />}
            label="Send welcome message (email and/or SMS when configured)"
          />
          <Divider sx={{ my: 0.5 }} />
          {mutation.isPending && <LinearProgress sx={{ borderRadius: 1 }} />}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button type="button" onClick={handleClose} disabled={mutation.isPending}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formDisabled}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {mutation.isPending ? 'Creating…' : `Create ${partnerLabel}`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
